import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { ForumTopic } from '../types';

const CommunityForum: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const forumCategories = [
  { id: 'all', name: 'All Topics', count: 1247 },
  { id: 'general', name: 'General Discussion', count: 342 },
  { id: 'technical', name: 'Technical Support', count: 456 },
  { id: 'integration', name: 'Integration Help', count: 189 },
  { id: 'feature-requests', name: 'Feature Requests', count: 123 },
  { id: 'best-practices', name: 'Best Practices', count: 137 }];


  const forumTopics: ForumTopic[] = [
  {
    id: '1',
    title: 'Best practices for calibrating people counting in high-traffic retail stores',
    author: 'Sarah Mitchell',
    authorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9e8814c-1763296696290.png",
    authorAvatarAlt: 'Professional woman with brown hair in business attire smiling at camera',
    category: 'best-practices',
    replies: 23,
    views: 1456,
    lastActivity: '2 hours ago',
    isResolved: true,
    isPinned: true
  },
  {
    id: '2',
    title: 'Integration with existing Milestone XProtect VMS - Connection timeout issues',
    author: 'Michael Rodriguez',
    authorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffbd3bcf-1763294660912.png",
    authorAvatarAlt: 'Man with short dark hair wearing glasses and blue shirt in office setting',
    category: 'technical',
    replies: 15,
    views: 892,
    lastActivity: '4 hours ago',
    isResolved: false,
    isPinned: false
  },
  {
    id: '3',
    title: 'Feature Request: Multi-zone heatmap analytics for large warehouse facilities',
    author: 'Jennifer Chen',
    authorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13dd7b4b8-1763293360912.png",
    authorAvatarAlt: 'Asian woman with long black hair in professional blazer smiling confidently',
    category: 'feature-requests',
    replies: 34,
    views: 2103,
    lastActivity: '6 hours ago',
    isResolved: false,
    isPinned: true
  },
  {
    id: '4',
    title: 'How to optimize GPU utilization for 50+ concurrent camera streams?',
    author: 'David Thompson',
    authorAvatar: "https://images.unsplash.com/photo-1596661730746-bab8891aa5e0",
    authorAvatarAlt: 'Young man with blonde hair in casual shirt outdoors with natural lighting',
    category: 'technical',
    replies: 18,
    views: 1234,
    lastActivity: '8 hours ago',
    isResolved: true,
    isPinned: false
  },
  {
    id: '5',
    title: 'Sharing our implementation success story: 40% reduction in queue times',
    author: 'Emily Watson',
    authorAvatar: "https://images.unsplash.com/photo-1592899436871-a50d9a3c1aac",
    authorAvatarAlt: 'Woman with red hair wearing green sweater smiling warmly at camera',
    category: 'general',
    replies: 42,
    views: 3567,
    lastActivity: '12 hours ago',
    isResolved: false,
    isPinned: true
  },
  {
    id: '6',
    title: 'API rate limiting best practices for real-time dashboard updates',
    author: 'Robert Kim',
    authorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17550fd67-1763295501645.png",
    authorAvatarAlt: 'Asian man with short black hair in formal suit with professional background',
    category: 'integration',
    replies: 11,
    views: 678,
    lastActivity: '1 day ago',
    isResolved: true,
    isPinned: false
  }];


  const filteredTopics = activeCategory === 'all' ?
  forumTopics :
  forumTopics.filter((topic) => topic.category === activeCategory);

  return (
    <section className="py-16 px-4 md:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Community Forum
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Connect with fellow camlytix AI users, share experiences, and get expert advice from our community of implementation specialists.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-card rounded-xl border border-border p-6 sticky top-24">
              <h3 className="font-bold text-foreground mb-4 flex items-center">
                <Icon name="Layers" size={20} className="mr-2" />
                Categories
              </h3>
              <div className="space-y-2">
                {forumCategories.map((category) =>
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-300 ${
                  activeCategory === category.id ?
                  'bg-primary text-primary-foreground' :
                  'bg-muted/50 text-text-secondary hover:bg-muted'}`
                  }>

                    <span className="text-sm font-medium">{category.name}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-background/20">
                      {category.count}
                    </span>
                  </button>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-border">
                <Button
                  variant="default"
                  size="default"
                  fullWidth
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => console.log('Create new topic')}>

                  New Topic
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="space-y-4">
              {filteredTopics.map((topic) =>
              <div
                key={topic.id}
                className="bg-card rounded-xl border border-border p-6 hover:shadow-brand-lg transition-all duration-300 cursor-pointer group"
                onClick={() => console.log(`Opening topic: ${topic.title}`)}>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Image
                      src={topic.authorAvatar}
                      alt={topic.authorAvatarAlt}
                      className="w-12 h-12 rounded-full object-cover" />

                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            {topic.isPinned &&
                          <Icon name="Pin" size={16} className="text-accent" />
                          }
                            {topic.isResolved &&
                          <div className="flex items-center space-x-1 px-2 py-1 bg-success/10 rounded-full">
                                <Icon name="CheckCircle2" size={14} className="text-success" />
                                <span className="text-xs font-medium text-success">Resolved</span>
                              </div>
                          }
                            <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                              {forumCategories.find((cat) => cat.id === topic.category)?.name}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                            {topic.title}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-text-secondary">
                            <span className="font-medium">{topic.author}</span>
                            <span>•</span>
                            <span>{topic.lastActivity}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-6 mt-4 pt-4 border-t border-border">
                        <div className="flex items-center space-x-2 text-sm text-text-secondary">
                          <Icon name="MessageSquare" size={16} />
                          <span>{topic.replies} replies</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-text-secondary">
                          <Icon name="Eye" size={16} />
                          <span>{topic.views.toLocaleString()} views</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {filteredTopics.length === 0 &&
            <div className="text-center py-12 bg-card rounded-xl border border-border">
                <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No topics found</h3>
                <p className="text-text-secondary mb-6">
                  Be the first to start a discussion in this category
                </p>
                <Button
                variant="default"
                size="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => console.log('Create new topic')}>

                  Create New Topic
                </Button>
              </div>
            }
          </div>
        </div>
      </div>
    </section>);

};

export default CommunityForum;